import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Mail } from 'lucide-react';
import namkeAuthBanner from '../assets/namke-auth-banner.png';
import namkeLogoHeader from '../assets/namke-logo-header.png';
import API_BASE_URL from '../config/api';

const inputClass =
  'w-full min-h-[56px] px-6 bg-namke-cream/60 border border-namke-black/10 rounded-full focus:outline-none focus:ring-2 focus:ring-namke-darkGreen/40 focus:border-transparent transition-colors text-namke-black placeholder-namke-black/40 text-base';

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.detail || t('auth.genericError'));
        return;
      }
      setDone(true);
    } catch {
      setError(t('auth.serverUnreachable'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 440px), 1fr))', background: '#FCFBF5' }}>
      {/* Colonne formulaire */}
      <div style={{ padding: 'clamp(24px, 4vw, 56px) clamp(20px, 5vw, 72px)', display: 'flex', flexDirection: 'column', gap: 'clamp(22px, 3vw, 36px)' }}>
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'flex-start' }}>
          <img src={namkeLogoHeader} alt="namke" style={{ height: '64px', width: 'auto', objectFit: 'contain' }} />
        </Link>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '26px', maxWidth: '440px', width: '100%', margin: '0 auto' }}>
          {done ? (
            <>
              <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '14px', alignItems: 'flex-start' }}>
                <span style={{ width: '56px', height: '56px', borderRadius: '999px', background: 'rgba(114,183,68,0.16)', display: 'grid', placeItems: 'center' }}>
                  <Mail size={24} color="#054625" />
                </span>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#EF4C23' }}>E-mail envoyé</span>
                <h1 style={{ margin: 0, fontWeight: 900, fontSize: 'clamp(30px, 3.6vw, 44px)', lineHeight: 1.05, letterSpacing: '-0.025em' }}>{t('auth.resetLinkSent')}</h1>
                <p style={{ margin: 0, fontSize: '16px', lineHeight: 1.6, color: 'rgba(5,16,4,0.74)' }}>
                  Vérifiez votre boîte de réception <strong style={{ color: '#054625' }}>{email}</strong> et suivez le lien pour choisir un nouveau mot de passe.
                </p>
              </div>

              <Link
                to="/login"
                className="animate-fade-in-delay"
                style={{
                  border: 'none', cursor: 'pointer', background: '#EF4C23', color: '#FCFBF5', textAlign: 'center',
                  fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '16.5px', minHeight: '58px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '999px',
                  transition: 'transform 150ms ease',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px) scale(1.01)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
              >
                {t('auth.backToLogin2')}
              </Link>
            </>
          ) : (
            <>
              <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#EF4C23' }}>Mot de passe oublié</span>
                <h1 style={{ margin: 0, fontWeight: 900, fontSize: 'clamp(32px, 3.8vw, 50px)', lineHeight: 1, letterSpacing: '-0.025em' }}>{t('auth.forgotPasswordTitle')}</h1>
                <p style={{ margin: 0, fontSize: '16px', lineHeight: 1.6, color: 'rgba(5,16,4,0.74)' }}>{t('auth.resetPasswordDesc')}</p>
              </div>

              {error && (
                <div className="form-message form-message--error text-center">{error}</div>
              )}

              <form onSubmit={handleSubmit} className="animate-fade-in-delay" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <label style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(5,16,4,0.6)' }}>
                    {t('auth.emailAddress')}
                  </span>
                  <input
                    type="email"
                    className={inputClass}
                    placeholder="vous@exemple.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    autoFocus
                  />
                </label>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    border: 'none', cursor: 'pointer', background: '#EF4C23', color: '#FCFBF5',
                    fontFamily: 'Poppins, sans-serif', fontWeight: 600, fontSize: '16.5px', minHeight: '58px', borderRadius: '999px', opacity: loading ? 0.7 : 1,
                    transition: 'transform 150ms ease, background-color 150ms ease',
                  }}
                  onMouseEnter={(e) => { if (!loading) e.currentTarget.style.transform = 'translateY(-1px) scale(1.01)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'none'; }}
                >
                  {loading ? t('auth.sending') : t('auth.sendLink')}
                </button>
              </form>

              <span className="animate-fade-in-delay-2" style={{ textAlign: 'center', fontSize: '15px', color: 'rgba(5,16,4,0.74)' }}>
                <Link to="/login" style={{ fontWeight: 600, color: '#054625' }}>{t('auth.backToLogin2')}</Link>
              </span>
            </>
          )}
        </div>

        <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12.5px', color: 'rgba(5,16,4,0.58)' }}>
          © 2026 namke · <Link to="/impressum" style={{ color: 'rgba(5,16,4,0.58)' }}>Mentions légales</Link> · <Link to="/privacy-policy" style={{ color: 'rgba(5,16,4,0.58)' }}>Confidentialité</Link>
        </span>
      </div>

      {/* Aside photo + témoignage */}
      <aside style={{ position: 'relative', background: '#FBF9D9', minHeight: '420px', display: 'flex', alignItems: 'flex-end', overflow: 'hidden' }} className="hidden lg:flex">
        <img src={namkeAuthBanner} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        <div className="animate-fade-in-delay-2" style={{ position: 'relative', margin: 'clamp(20px, 2.5vw, 36px)', background: 'rgba(5,70,37,0.92)', color: '#FBF9D9', borderRadius: '34px', padding: 'clamp(22px, 2.6vw, 34px)', display: 'flex', flexDirection: 'column', gap: '14px', maxWidth: '460px', backdropFilter: 'blur(6px)' }}>
          <span style={{ fontFamily: 'Archivo, sans-serif', fontSize: '12px', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#FFC717' }}>{t('auth.resetPasswordTitle')}</span>
          <p style={{ margin: 0, fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: 'clamp(20px, 2vw, 27px)', lineHeight: 1.35 }}>
            « Un lien, un nouveau mot de passe, et je retrouve mes marmites favorites en deux minutes. »
          </p>
          <span className="flex items-center gap-3" style={{ fontFamily: 'Archivo, sans-serif', fontSize: '13.5px', color: 'rgba(251,249,217,0.8)' }}>
            <span style={{ width: '34px', height: '34px', borderRadius: '999px', background: '#FFC717' }} />
            Awa D. · quatrième commande
          </span>
        </div>
      </aside>
    </div>
  );
}
